import { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Navbar } from 'react-bootstrap';
import { ProfileMenu } from '../home/ProfileMenu';
import { AppContext } from '../../context/AppContext';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';
import { generateChatSessionId } from '../../utils/chat';
import { logout } from '../../services/authentication';
import { menuConfig } from '../home/menuConfig';
import { CommonDropdown } from '../common/UI/CommonDropdown';
import ExpandCollapseIcon from '../../assets/icon-hamburger.svg?react';

import logo from '../../assets/Elysia-logo.svg';
import './style.scss';
import { AuthContext } from '../../context/AuthContext';
import AppAgentHeading from '../app-builder/AppAgentHeading';
import useElysiaStore from '@/app/elysia-store';
import { SettingContext } from '@/context/SettingContext';
import AppActionsDropdown from '../app-builder/AppActionsDropdown';

const Menu = () => {
  const navigate = useNavigate();

  const { setIsSideNavOpen, allowedRoutes = [] } = useContext(AppContext);
  const { userInitials = '' } = useContext(AuthContext);
  const location = useLocation();

  const isPreview = useElysiaStore((state) => state.isPreview);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const name = useElysiaStore((state) => state.name);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const isChatActive = useElysiaStore((state) => state.isChatActive);
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setSources = useElysiaStore.getState().setSources;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setWordsCount = useElysiaStore.getState().setWordsCount;
  const setWordCountError = useElysiaStore.getState().setWordCountError;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setIsAppTerminated = useElysiaStore.getState().setIsAppTerminated;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const setCategoryCount = useElysiaStore.getState().setCategoryCount;
  const resetAIApp = useElysiaStore.getState().resetAIApp;

  const { totalImageQuota, availableImageQuota, defaultModel, setSelectedModel } =
    useContext(SettingContext);

  const isDashboardRouteAllowed = allowedRoutes?.includes('/dashboard');
  const isSandboxRouteAllowed = allowedRoutes?.includes('/sandbox');
  const config = menuConfig(
    navigate,
    logout,
    userInitials,
    isSandboxRouteAllowed,
    isDashboardRouteAllowed
  );
  const userMenu = config.logoutMenu;
  const showControls =
    location.pathname === '/' ||
    location.pathname === '/session' ||
    location.pathname.startsWith('/session/');

  // Remove "View Profile" and "Sandbox" options from the user menu on small screens
  if (userMenu) {
    userMenu.dropdownItems =
      userMenu?.dropdownItems.filter(
        (item) => item.text !== 'View Profile' && item.text !== 'Sandbox'
      ) || [];
  }

  const startNewTopicChat = (exitApp = false) => {
    useElysiaStore.setState({ isStreamingStopped: false });
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    if (chatAgent !== 'app-agent' || exitApp) {
      setChatAgent?.('Generic');
      setIsAppTerminated(true);
      resetAIApp();
      setSelectedModel(defaultModel);
      setBrandDoc(false);
    }
    setIsChatActive?.(false);
    setShowScrollIcon?.(false);
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    navigate('/', { replace: true });
    setPromptInput('');
    setSessionPath?.('');
    setIsRestrictedChatSessionStatus?.('');
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setSources?.([]);
    setSelectedSources?.([]);
    setWordCountError?.(false);
    setWordsCount?.(0);
    setShowFileToasts?.(false);
    setIsChatDisabled?.(false);
    setCategoryCount({});
  };

  const OpenSideNav = (e) => {
    setIsSideNavOpen(true);
    e.stopPropagation();
  };

  const MobileMenu = () => {
    return (
      <Button
        aria-label="side panel"
        data-testid="side-panel-icon-button-mobile"
        className="collapse-btn-wrapper fw-semibold"
        onClick={OpenSideNav}
      >
        <ExpandCollapseIcon />
      </Button>
    );
  };
  return (
    <>
      <Navbar className="custom-nav d-none d-lg-flex">
        <Navbar.Brand className="flex items-center cursor-pointer me-0 me-md-auto">
          <Button variant="link" onClick={() => startNewTopicChat(true)}>
            <img className="gemini-logo" alt="Elysia" src={logo} />
          </Button>
        </Navbar.Brand>
        <div className="text-end">
          <ProfileMenu />
        </div>
      </Navbar>

      <Navbar className="custom-nav d-flex d-lg-none justify-content-between align-items-center">
        <div className="b-block d-lg-none open-sidebar-mobile">
          <MobileMenu />
        </div>
        <Navbar.Brand className="flex items-center cursor-pointer mobile-branding">
          <Button variant="link" onClick={() => startNewTopicChat(true)}>
            <img className="gemini-logo" alt="Elysia" src={logo} />
          </Button>
        </Navbar.Brand>
        <div className="profile-menu">{userMenu && <CommonDropdown {...userMenu} />}</div>
      </Navbar>

      {showControls && aiAppId && (
        <div className="flex-grow-1 d-flex justify-content-center me-2 position-relative">
          <div className="width-control d-flex justify-content-between">
            <div>
              {chatAgent === 'app-agent' && !isPreview && !isAppPreview && isChatActive && (
                <div className="d-flex align-items-center">
                  <AppAgentHeading />
                  <AppActionsDropdown aiAppId={aiAppId} />
                </div>
              )}
            </div>

            {chatAgent === 'app-agent' && !isPreview && !isAppPreview && (
              <div className={`${name === 'Image Generator' ? 'image-gen-header' : ''}`}>
                <Button
                  className="exit-app"
                  data-testid="exit-app-button"
                  onClick={() => startNewTopicChat(true)}
                >
                  <div className="exit-app-text">Exit App</div>
                </Button>
              </div>
            )}
          </div>

          {/* Counter always on screen right edge */}
          {name === 'Image Generator' && (
            <div className="position-absolute end-0">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="tooltip-sidebar">
                    You can generate up to {totalImageQuota} images per calendar month. The count
                    resets at the start of each month, and unused images do not roll over.
                  </Tooltip>
                }
              >
                <Button className="counter-btn me-2">
                  {availableImageQuota}/{totalImageQuota}
                </Button>
              </OverlayTrigger>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Menu;
